import { NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import moment from 'moment-mini';
import { finalize, take } from 'rxjs/operators';
import { ConfigurationService } from '../../services/configuration.service';
import { EpiService } from '../../services/epi/epi.service';
import { GtmService } from '../../services/gtm.service';
import { JobsiteService } from '../../services/jobsite/jobsite.service';
import { OrderReturnUtilsService } from '../../services/order-return-utils.service';
import { TransportPricingOrderReturnRequest } from '../../services/transport-pricing/models/transport-pricing-order-return-request.model';
import { TransportPricingService } from '../../services/transport-pricing/transport-pricing.service';
import { Configurations, FeatureToggles } from '../../types/configuration.types';
import { ReturnProduct } from '../../types/gtm.types';
import { CreateOrderReturnTransportItem, InnerOrderRow, OrderRow, TransformedOrderRow } from '../../types/order.types';
import { AppData } from '../../types/translations.types';
import { CalendarOptions } from '../calendar/calendar-options';
import { CalendarComponent } from '../calendar/calendar.component';
import { IconComponent } from '../icon/icon.component';
import { LoadingAreaComponent } from '../loading-area/loading-area.component';
import { PriceDisplayComponent } from '../price-display/price-display.component';
import { StepperOldComponent } from '../stepper-old/stepper-old.component';
import { ReturnOrderTransportCostResult } from './types/return-order.types';

@Component({
	selector: 'cramo-return-order',
	templateUrl: './return-order.component.html',
	styleUrls: ['./return-order.component.scss'],
	imports: [
		NgIf,
		StepperOldComponent,
		IconComponent,
		MatSlideToggleModule,
		FormsModule,
		CalendarComponent,
		LoadingAreaComponent,
		PriceDisplayComponent,
	],
})
export class ReturnOrderComponent implements OnInit {
	public appData: Signal<AppData>;
	public transportInstructions: string;
	public sendingRequest = false;
	public showTransportInstructions = false;
	public disableReturnDateSelection: boolean;
	public calendarOptions = new CalendarOptions(moment(), null, true, moment(), null, []);
	public isSuccess = true;
	private configurations: Configurations;
	public transportCostErrorMessage: string | null = null;
	public isloadingTransportPricing = false;
	public isInformationMessage = false;

	public transportCostResult: ReturnOrderTransportCostResult;
	public featureToggles: Signal<FeatureToggles>;

	public orderImages: Record<OrderRow['OrderId'], { imageUrl: string; hasImage: boolean }> = {};

	@Input() public ordersToReturn: (Omit<TransformedOrderRow, 'OrderRow'> & {
		OrderRow: TransformedOrderRow['OrderRow'] & { DeliveryNotes: string | null };
	})[]; //TODO -> SHOULD BE ORDER ROW
	@Input() public jobsiteAddress: { Zip: string; StreetAddress: string; City: string };
	@Output() public closePanel: EventEmitter<boolean> = new EventEmitter();
	@Output() public returnSuccess: EventEmitter<OrderRow[]> = new EventEmitter();

	constructor(
		private epiService: EpiService,
		public dialogRef: MatDialogRef<ReturnOrderComponent>,
		private orderReturnUtils: OrderReturnUtilsService,
		private jobsiteService: JobsiteService,
		private gtmService: GtmService,
		private transportPricingService: TransportPricingService,
		private configurationService: ConfigurationService,
	) {
		this.configurations = this.configurationService.configurations;
		this.appData = toSignal(this.epiService.appData$, { requireSync: true });
		this.featureToggles = toSignal(this.configurationService.featureToggles$, { requireSync: true });
	}

	ngOnInit() {
		this.disableReturnDateSelection = this.orderReturnUtils.getReturnDateDisabled();

		const firstDate = this.orderReturnUtils.getFirstDateForOrderPickup();
		this.calendarOptions = new CalendarOptions(
			firstDate,
			null,
			true,
			firstDate,
			null,
			this.configurations.calendarHolidays,
			null,
			true,
			false,
			false,
			false,
			null,
			false,
			true,
		);

		this.ordersToReturn.forEach((order) => {
			const imageUrl = this.getProductImageUrl(order);
			this.orderImages[order.OrderId] = { imageUrl, hasImage: imageUrl.includes('icon_nophoto') };
		});

		this.attemptToCalculateTransportCost();
	}

	public onReturnDateChange(): void {
		this.attemptToCalculateTransportCost();
	}

	private attemptToCalculateTransportCost() {
		if (!this.featureToggles().price.isReturnTransportPricingEnabled) {
			return;
		}

		this.isloadingTransportPricing = true;

		const items = this.ordersToReturn.map((order) => {
			return {
				orderId: order.OrderRow.OrderId,
				orderRowId: order.OrderRow.Id,
				quantity: order.stepperOptions.value,
			};
		});

		const request: TransportPricingOrderReturnRequest = {
			returnDate: this.calendarOptions.startDate!.format('YYYY-MM-DD'),
			items: items,
		};

		this.transportPricingService
			.getTransportCostForOrderReturn(request)
			.pipe(take(1))
			.subscribe((response) => {
				this.transportCostResult = {
					price: response.price,
					vat: response.vat,
					vatPercentage: response.vatPercentage,
					calculationResult: response.calculationResult,
				};

				this.setTransportPriceError(this.transportCostResult.calculationResult);

				this.isloadingTransportPricing = false;
			});
	}

	private setTransportPriceError(calculationResult: number) {
		switch (calculationResult) {
			case 1:
				this.transportCostErrorMessage = null;
				break;
			case 2:
				this.transportCostErrorMessage = this.appData().jobsites.returnTransportPricePartialCalculatedText;
				break;
			case 4:
				this.transportCostErrorMessage = this.appData().checkout.transportPricingPostalCodeError;
				break;
			default:
				this.transportCostErrorMessage = this.appData().jobsites.returnTransportPriceUnableToCalculateText;
				break;
		}
	}

	public changeOrderQuantityValue(order: TransformedOrderRow, quantity: number) {
		order.stepperOptions.value = quantity;
	}

	public returnOrders() {
		const items: CreateOrderReturnTransportItem[] = [];
		const dataLayerReturns: ReturnProduct[] = [];

		this.sendingRequest = true;
		const transportDate = this.calendarOptions.startDate!.toDate();

		if (this.ordersToReturn) {
			this.ordersToReturn.forEach((order) => {
				if (order.stepperOptions.value > 0) {
					this.addToDatalayer(dataLayerReturns, order);

					items.push({
						orderId: order.OrderId,
						orderRowId: order.OrderRow.Id,
						quantity: order.stepperOptions.value,
					});

					order.OrderRow.RequestedReturnQuantity = order.stepperOptions.value;
					order.OrderRow.PickUpFromJobSiteRequestedDate = transportDate.toISOString();
					order.OrderRow.DeliveryNotes = this.showTransportInstructions ? this.transportInstructions : null;
				}
			});
		}

		this.gtmService.orderReturn(dataLayerReturns);

		this.jobsiteService
			.requestOrderPickup({
				items: items,
				pickupInstructions: this.showTransportInstructions ? this.transportInstructions : null,
				requestedPickupDate: transportDate,
			})
			.pipe(
				take(1),
				finalize(() => {
					this.returnSuccess.emit(this.ordersToReturn);
					this.sendingRequest = false;
					this.closePanel.emit(true);
				}),
			)
			.subscribe((isPickupRequested) => {
				if (!isPickupRequested) {
					this.isSuccess = false;
					return;
				}
			});
	}

	public cancel() {
		this.closePanel.emit(true);
	}

	public toggleTransportInstructions() {
		this.showTransportInstructions = !this.showTransportInstructions;
	}

	private addToDatalayer(array: ReturnProduct[], order: unknown) {
		const typeCastOrder = order as Omit<TransformedOrderRow, 'OrderRow'> & {
			OrderRow: InnerOrderRow & { ProductId: string };
		};

		array.push({
			name: typeCastOrder.OrderRow.ProductName,
			id: typeCastOrder.OrderRow.ProductId,
			quantity: typeCastOrder.stepperOptions.value,
		});
	}

	private getProductImageUrl(order: TransformedOrderRow): string {
		let url = order.product?.files?.images[0].url ?? '';
		url = order.serialImage ? order.serialImage : url;
		if (url.length === 0) {
			url = '/assets/icons/icon_nophoto.svg';
		} else {
			url = `${url}?width=160`;
		}
		return url;
	}
}
